import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FaWhatsapp, 
  FaCheckCircle, 
  FaClock, 
  FaUsers,
  FaIndustry,
  FaChartLine,
  FaArrowLeft,
  FaSync,
  FaComments,
  FaPhoneAlt,
  FaEnvelope,
  FaCalendarAlt,
  FaRocket
} from 'react-icons/fa';
import { motion } from 'framer-motion';
import { readySystems } from '../data/readySystems';

const SystemDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const system = readySystems.find(sys => sys.id === parseInt(id));

  if (!system) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#000324] via-[#010536] to-[#020a5c] flex items-center justify-center px-4">
        <div className="text-center">
          <div className="text-6xl mb-6">🔍</div>
          <h2 className="text-3xl font-bold text-white mb-4">System Not Found</h2>
          <p className="text-white/70 mb-8">The system you're looking for doesn't exist or has been moved.</p>
          <button 
            onClick={() => navigate('/ready-systems')} 
            className="inline-flex items-center px-6 py-3 rounded-xl bg-gradient-to-r from-[#fdaa01] to-[#ffc042] text-[#010536] font-semibold hover:shadow-lg transition-all duration-300"
          >
            <FaArrowLeft className="mr-2" />
            Back to Systems
          </button>
        </div>
      </div>
    );
  }

  const getWhatsAppMessage = () => {
    return encodeURIComponent(
      `Hello Shyee Codes! I'm interested in the ${system.name} system.\n\n` +
      `Category: ${system.category}\n` +
      `Implementation Time: ${system.implementationTime}\n\n` +
      `Please share more details about this system.`
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#000324] via-[#010536] to-[#020a5c]">
      {/* Hero Section */}
      <section className="relative py-16 lg:py-20 overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_50%,rgba(253,170,1,0.1)_0%,transparent_50%),radial-gradient(circle_at_80%_20%,rgba(99,102,241,0.1)_0%,transparent_50%)]" />
        <div className="absolute top-20 left-10 w-64 h-64 bg-[#fdaa01]/10 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-20 right-10 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl animate-float [animation-delay:5s]" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => navigate('/ready-systems')}
            className="inline-flex items-center text-white/70 hover:text-[#fdaa01] transition-colors duration-300 mb-8"
          >
            <FaArrowLeft className="mr-2" />
            Back to All Systems
          </motion.button>
          
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="flex items-start gap-6"
            >
              <div 
                className="w-20 h-20 sm:w-24 sm:h-24 rounded-3xl flex items-center justify-center flex-shrink-0 shadow-xl"
                style={{ 
                  backgroundColor: `${system.color}20`,
                  color: system.color,
                  border: `2px solid ${system.color}30`
                }}
              >
                <span className="text-4xl">{system.icon}</span>
              </div>
              
              <div>
                <span 
                  className="inline-block text-xs font-semibold px-4 py-2 rounded-full mb-3"
                  style={{ 
                    backgroundColor: `${system.color}15`,
                    color: system.color
                  }}
                >
                  {system.category}
                </span>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-4">
                  {system.name}
                </h1>
                <div className="flex flex-wrap gap-2">
                  {system.tags?.map((tag, index) => (
                    <span 
                      key={index}
                      className="text-xs font-semibold px-3 py-1 rounded-full bg-white/10 text-white/80 border border-white/20"
                    >
                      {tag}
                    </span>
                  ))}
                  {system.popular && (
                    <span className="text-xs font-semibold px-3 py-1 rounded-full bg-[#fdaa01] text-[#010536]">
                      🔥 Popular
                    </span>
                  )}
                  {system.featured && (
                    <span className="text-xs font-semibold px-3 py-1 rounded-full bg-blue-500 text-white">
                      ⭐ Featured
                    </span>
                  )}
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <a
                href="https://calendar.app.google/Ti8ismLSofY2gGci8"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-8 py-4 rounded-xl bg-gradient-to-r from-[#fdaa01] to-[#ffc042] text-[#010536] font-semibold shadow-lg shadow-[#fdaa01]/30 hover:shadow-xl hover:shadow-[#fdaa01]/40 hover:scale-105 transition-all duration-300"
              >
                <FaCalendarAlt className="mr-2" />
                Schedule Free Demo
              </a>
              
              <a
                href={`https://wa.me/254762272152?text=${getWhatsAppMessage()}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-8 py-4 rounded-xl bg-green-500 text-white font-semibold shadow-lg shadow-green-500/30 hover:shadow-xl hover:shadow-green-500/40 hover:scale-105 transition-all duration-300"
              >
                <FaWhatsapp className="mr-2" />
                WhatsApp Us
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Details */}
          <div className="lg:col-span-2 space-y-8">
            {/* Overview */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-3xl p-8 shadow-xl"
            >
              <h2 className="text-2xl font-bold text-[#010536] mb-6 flex items-center gap-3">
                <FaIndustry className="text-[#fdaa01]" />
                System Overview
              </h2>
              <p className="text-gray-700 leading-relaxed mb-8">{system.description}</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-red-50 rounded-2xl p-6 border border-red-100">
                  <h3 className="flex items-center gap-2 font-bold text-red-600 mb-3">
                    <FaIndustry />
                    The Challenge
                  </h3>
                  <p className="text-gray-700 leading-relaxed">{system.challenge}</p>
                </div>
                <div className="bg-green-50 rounded-2xl p-6 border border-green-100">
                  <h3 className="flex items-center gap-2 font-bold text-green-600 mb-3">
                    <FaCheckCircle />
                    Our Solution
                  </h3>
                  <p className="text-gray-700 leading-relaxed">{system.solution}</p>
                </div>
              </div>
            </motion.section>

            {/* Features */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-3xl p-8 shadow-xl"
            >
              <h2 className="text-2xl font-bold text-[#010536] mb-6 flex items-center gap-3">
                <FaRocket className="text-[#fdaa01]" />
                Key Features
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {system.features?.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-3 bg-gray-50 rounded-xl p-4 hover:bg-white hover:shadow-lg transition-all duration-300 border border-transparent hover:border-gray-200"
                  >
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ 
                        backgroundColor: `${system.color}15`,
                        color: system.color
                      }}
                    >
                      {feature.icon}
                    </div>
                    <span className="text-gray-700">{feature.text}</span>
                  </motion.div>
                ))}
              </div>
            </motion.section>

            {/* Ideal For */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-3xl p-8 shadow-xl"
            >
              <h2 className="text-2xl font-bold text-[#010536] mb-6 flex items-center gap-3">
                <FaUsers className="text-[#fdaa01]" />
                Ideal For
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {system.industries?.map((industry, index) => (
                  <div 
                    key={index}
                    className="flex items-center gap-3 bg-gray-50 rounded-xl p-4 hover:bg-white hover:shadow-lg transition-all duration-300"
                  >
                    <FaUsers style={{ color: system.color }} className="flex-shrink-0" />
                    <span className="text-gray-700">{industry}</span>
                  </div>
                ))}
              </div>
            </motion.section>
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-8">
            {/* Quick Stats */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-3xl p-8 shadow-xl"
            >
              <h3 className="text-xl font-bold text-[#010536] mb-6 flex items-center gap-3">
                <FaChartLine className="text-[#fdaa01]" />
                Quick Stats
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {system.results?.map((result, index) => (
                  <div 
                    key={index}
                    className="text-center bg-gray-50 rounded-xl p-4 hover:bg-white hover:shadow-lg transition-all duration-300"
                  >
                    <div className="text-2xl font-extrabold text-[#010536] mb-1">
                      {result.metric}
                    </div>
                    <div className="text-xs text-gray-600">
                      {result.label}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Implementation Details */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-3xl p-8 shadow-xl"
            >
              <h3 className="text-xl font-bold text-[#010536] mb-6 flex items-center gap-3">
                <FaClock className="text-[#fdaa01]" />
                Implementation Details
              </h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3 bg-gray-50 rounded-xl p-4">
                  <FaClock className="text-[#fdaa01] flex-shrink-0 mt-1" />
                  <div>
                    <strong className="block text-[#010536] mb-1">Time to Launch:</strong>
                    <span className="text-gray-600">{system.implementationTime}</span>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 bg-gray-50 rounded-xl p-4">
                  <FaSync className="text-[#fdaa01] flex-shrink-0 mt-1" />
                  <div>
                    <strong className="block text-[#010536] mb-1">Project Duration:</strong>
                    <span className="text-gray-600">{system.duration}</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Contact Support */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-br from-[#010536] to-[#020a5c] rounded-3xl p-8 text-white shadow-xl border border-[#fdaa01]/10"
            >
              <h3 className="text-xl font-bold mb-3 flex items-center gap-3">
                <FaComments className="text-[#fdaa01]" />
                Need Help?
              </h3>
              <p className="text-white/70 mb-6">
                Our experts are ready to answer your questions
              </p>
              <div className="space-y-3">
                <a 
                  href={`https://wa.me/254762272152?text=${getWhatsAppMessage()}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 bg-green-500 text-white rounded-xl p-4 font-semibold hover:bg-green-600 hover:shadow-lg transition-all duration-300"
                >
                  <FaWhatsapp className="text-xl" />
                  WhatsApp Chat
                </a>
                <a 
                  href="tel:+254762272152" 
                  className="flex items-center gap-3 bg-[#fdaa01] text-[#010536] rounded-xl p-4 font-semibold hover:bg-[#ffc042] hover:shadow-lg transition-all duration-300"
                >
                  <FaPhoneAlt className="text-xl" />
                  Call Now
                </a>
                <a 
                  href="https://calendar.app.google/Ti8ismLSofY2gGci8"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 bg-white/10 border border-white/20 text-white rounded-xl p-4 font-semibold hover:bg-white/20 hover:shadow-lg transition-all duration-300"
                >
                  <FaCalendarAlt className="text-xl" />
                  Schedule Call
                </a>
                <a 
                  href="mailto:sheilabwkabiro@gmail.com" 
                  className="flex items-center gap-3 bg-white/10 border border-white/20 text-white rounded-xl p-4 font-semibold hover:bg-white/20 hover:shadow-lg transition-all duration-300"
                >
                  <FaEnvelope className="text-xl" />
                  Email Us
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Bottom CTA Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-[#010536] to-[#020a5c] rounded-3xl p-8 sm:p-12 text-center shadow-2xl border border-[#fdaa01]/10"
          >
            <div className="text-6xl mb-6">🚀</div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Ready to Get Started with {system.name}?
            </h2>
            <p className="text-white/70 mb-8 max-w-2xl mx-auto">
              Book a free strategy call and discover how this system can transform your business operations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://calendar.app.google/Ti8ismLSofY2gGci8"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-8 py-4 rounded-xl bg-gradient-to-r from-[#fdaa01] to-[#ffc042] text-[#010536] font-semibold shadow-lg shadow-[#fdaa01]/30 hover:shadow-xl hover:shadow-[#fdaa01]/40 hover:scale-105 transition-all duration-300"
              >
                <FaCalendarAlt className="mr-2" />
                Book Free Strategy Call
              </a>
              <a
                href={`https://wa.me/254762272152?text=${getWhatsAppMessage()}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-8 py-4 rounded-xl bg-green-500 text-white font-semibold shadow-lg shadow-green-500/30 hover:shadow-xl hover:shadow-green-500/40 hover:scale-105 transition-all duration-300"
              >
                <FaWhatsapp className="mr-2" />
                Chat on WhatsApp
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default SystemDetailPage;